import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigationLinks, memberOnlyLinks } from '../data/mock';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isScrolled = scrolled || location.pathname.startsWith('/admin') || location.pathname.startsWith('/user/dashboard');
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group flex-shrink-0">
            <img
              src="/images/csed-logo.png"
              alt="CSED Logo"
              className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#E87722] bg-[#E87722]/10'
                    : isScrolled
                    ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                    : 'text-white hover:text-[#E87722] hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Member-only Links - Show only when logged in */}
            {user && memberOnlyLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#E87722] bg-[#E87722]/10'
                    : isScrolled
                    ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                    : 'text-white hover:text-[#E87722] hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Membership Link (visitors only) */}
            {!user && (
            <Link
              to="/membership"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                location.pathname.startsWith('/membership')
                  ? 'text-[#E87722] bg-[#E87722]/10'
                  : isScrolled
                  ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  : 'text-white hover:text-[#E87722] hover:bg-white/10'
              }`}
            >
              Membership
            </Link>
            )}
            
            {/* Funding Options Link */}
            <Link
              to="/funding-options"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                location.pathname.startsWith('/funding-options')
                  ? 'text-[#E87722] bg-[#E87722]/10'
                  : isScrolled
                  ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  : 'text-white hover:text-[#E87722] hover:bg-white/10'
              }`}
            >
              Funding Options
            </Link>

            {/* Admin Dashboard Link - Only show for admins */}
            {user && user.role === 'admin' && (
              <>
                <Link
                  to="/admin"
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    location.pathname.startsWith('/admin')
                      ? 'text-[#E87722] bg-[#E87722]/10'
                      : isScrolled
                      ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                      : 'text-white hover:text-[#E87722] hover:bg-white/10'
                  }`}
                >
                  📊 Admin Dashboard
                </Link>
              </>
            )}
            
            {/* User Dashboard Link - Only show for regular users */}
            {user && user.role !== 'admin' && (
              <Link
                to="/user/dashboard"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  location.pathname === '/user/dashboard'
                    ? 'text-[#E87722] bg-[#E87722]/10'
                    : isScrolled
                    ? 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                    : 'text-white hover:text-[#E87722] hover:bg-white/10'
                }`}
              >
                📚 My Dashboard
              </Link>
            )}
          </nav>

          {/* Auth & CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {user ? (
              <>
                <span className={`text-sm ${isScrolled ? 'text-gray-600' : 'text-white'}`}>
                  {user.name}
                </span>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  asChild
                  variant="outline"
                  className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
                >
                  <Link to="/login">
                    <User className="h-4 w-4 mr-2" />
                    Login
                  </Link>
                </Button>
                <Button
                  asChild
                  className="bg-[#E87722] hover:bg-[#E87722]/90 text-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  <Link to="/engage">Contact Us</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              isScrolled ? 'text-[#1E3A5F]' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-2">
              {navigationLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#E87722] bg-[#E87722]/10'
                      : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Member-only Links - Show only when logged in */}
              {user && memberOnlyLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#E87722] bg-[#E87722]/10'
                      : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Membership Link (visitors only) */}
              {!user && (
              <Link
                to="/membership"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                  location.pathname.startsWith('/membership')
                    ? 'text-[#E87722] bg-[#E87722]/10'
                    : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                }`}
              >
                Membership
              </Link>
              )}
              
              {/* Quick Payment Link */}
              
              {/* Funding Options Link */}
              <Link
                to="/funding-options"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                  location.pathname.startsWith('/funding-options')
                    ? 'text-[#E87722] bg-[#E87722]/10'
                    : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                }`}
              >
                Funding Options
              </Link>
              
              {/* Admin Dashboard Link - Only show for admins */}
              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'text-[#E87722] bg-[#E87722]/10'
                      : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  }`}
                >
                  📊 Admin Dashboard
                </Link>
              )}
              
              {/* User Dashboard Link - Only show for regular users */}
              {user && user.role !== 'admin' && (
                <Link
                  to="/user/dashboard"
                  data-testid="mobile-user-dashboard-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === '/user/dashboard'
                      ? 'text-[#E87722] bg-[#E87722]/10'
                      : 'text-[#1E3A5F] hover:text-[#E87722] hover:bg-[#E87722]/5'
                  }`}
                >
                  📚 My Dashboard
                </Link>
              )}
              
              {/* Auth Buttons */}
              {user ? (
                <>
                  <div className="px-4 py-2 text-sm text-gray-600">
                    Logged in as {user.name}
                  </div>
                  <Button
                    onClick={handleLogout}
                    className="mx-4 bg-[#E87722] hover:bg-[#E87722]/90 text-white"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    asChild
                    variant="outline"
                    className="mx-4 border-[#E87722] text-[#E87722]"
                  >
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                      <User className="h-4 w-4 mr-2" />
                      Login
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className="mx-4 bg-[#E87722] hover:bg-[#E87722]/90 text-white mt-2"
                  >
                    <Link to="/engage" onClick={() => setIsMobileMenuOpen(false)}>
                      Contact Us
                    </Link>
                  </Button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
